import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MaterialModule } from '@huge-monorepo/shared/modules';
import { it } from 'node:test';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { SharedGenericSingletonService } from '@huge-monorepo/shared/services';
import { ICustomerGenericService, IHIPGenericService } from '@huge-monorepo/shared/interfaces';
import { ToastrService } from 'ngx-toastr';
import { SharedTeltonikaSingletonService } from '@huge-monorepo/shared/services';
import { IHIPTeltonikaService, ICustomerTeltonikaService } from '@huge-monorepo/shared/interfaces';
import { GenericTableHeaderComponent } from '@huge-monorepo/shared/components';

@Component({
  selector: 'huge-monorepo-teltonika-update-device-polling-modal',
  standalone: true,
  imports: [CommonModule, MaterialModule],
  templateUrl: './teltonika-update-device-polling-modal.component.html',
  styleUrl: './teltonika-update-device-polling-modal.component.scss',
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
  providers: [DatePipe]
})
export class TeltonikaUpdateDevicePollingModalComponent implements OnInit, OnDestroy {
  teltonikaService = this.sharedTeltonikaService.getService();
  isHIP = this.sharedTeltonikaService.getIsHIP();
  sessionGuid: string = '';
  devices: { IP: string, Status: TeltonikaTaskState, Responses?: { Message?: string }[]}[] = [];
  private socket: WebSocket;
  teltonikaStates = TeltonikaTaskState;
  columnsToDisplay = ['Status', 'DeviceIP'];
  columnsToDisplayWithExpand = [...this.columnsToDisplay, 'expand'];
  expandedElement:  { IP: string, Status: TeltonikaTaskState, Responses?: { Message?: string }[] }[]  | null;
  isWebsocketClosed: boolean = false;

  csv: string | null = null;
  user: string | null = null;
  dateString = this.datePipe.transform(new Date(), 'yyyyMMddHHmmss');

  constructor(
    public dialogRef: MatDialogRef<TeltonikaUpdateDevicePollingModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { sessionGuid: string, devices: { IP: string, Status: TeltonikaTaskState }[] },
    private toastr: ToastrService,
    private sharedTeltonikaService: SharedTeltonikaSingletonService<ICustomerTeltonikaService | IHIPTeltonikaService>,
    private datePipe: DatePipe,
  ) {
    dialogRef.disableClose = true;
    this.sessionGuid = data.sessionGuid;
    this.devices = data.devices.map(device => ({
      ...device,
    }));
  }

  ngOnInit(): void {
    this.openWebSocket();
  }

  ngOnDestroy(): void {
    this.closeWebSocket();
  }

  openWebSocket(): void {
    this.devices.forEach((device) => {
      device.Status = TeltonikaTaskState.loading;
    });

    this.socket = this.teltonikaService.openTeltonikaUpdateDevicePollingWebsocket(this.sessionGuid);
    this.isWebsocketClosed = false;

    this.socket.onmessage = (event) => {
      const jsonObject = JSON.parse(event.data);
      if (jsonObject["Successes"] && jsonObject["Successes"].length > 0) {
        this.devices.forEach(device => {
          jsonObject["Successes"].forEach((item) => {
            if (item["DeviceIP"] === device.IP) {
              device.Status = TeltonikaTaskState.success;

              if (item["Responses"]) {
                device.Responses = item["Responses"].map(response => ({
                  Message: response["Message"],
                  ResponseCode: response["ResponseCode"],
                }));
              } else {
                device.Responses = [];
              }
            }
          });
        });
      }
      if (jsonObject["Failures"] && jsonObject["Failures"].length > 0) {
        this.devices.forEach(device => {
          jsonObject["Failures"].forEach((item) => {
            if (item["DeviceIP"] === device.IP) {
              device.Status = TeltonikaTaskState.failure;

              if (item["Responses"]) {
                device.Responses = item["Responses"].map(response => ({
                  Message: response["Message"],
                  ResponseCode: response["ResponseCode"],
                }));
              } else {
                device.Responses = [];
              }
            }
          });
        });
      }

      if (jsonObject["CreatedBy"]) {
        this.user = jsonObject["CreatedBy"];
      }

      if (jsonObject["ResponseCSV"]) {
        this.csv = jsonObject["ResponseCSV"];
      }
    }

    this.socket.onclose = (event) => {
      this.isWebsocketClosed = true;
    }
  }

  closeWebSocket(): void {
    if (this.socket) {
      this.socket.close(1000);
    }
  }

  close(): void {
    this.closeWebSocket();
    this.dialogRef.close();
  }

  expand(device) {
    if (device.Responses && device.Responses.length > 0) {
      this.expandedElement = this.expandedElement === device ? null : device
    }
  }

  /**
   * Exports a base64-encoded CSV string as a downloadable file.
   * Decodes the base64 data, creates a Blob, and triggers a download in the browser.
   * @param base64Data - The base64-encoded CSV string.
   * @param filename - The desired filename for the downloaded CSV.
   */
  exportCSV(base64Data: string, filename: string) {
    const csvData = atob(base64Data);

    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });

    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}

export enum TeltonikaTaskState {
  loading,
  success,
  failure,
  cancelled,
}
